import { hsReadProjectFileLines } from './project-file-lines.function';

export interface HsRvmAttFile {
  file: File;
  path: string;
}

export interface HsRvmAttPair {
  name: string;
  rvm: HsRvmAttFile;
  att: HsRvmAttFile | null;
  processingId: string | null;
  error: string | null;
}

export interface HsRvmAttProject {
  pairs: HsRvmAttPair[];
  totalBytes: number;
  errors: string[];
  warnings: string[];
}

async function validateRvm(entry: HsRvmAttFile): Promise<void> {
  const header: Uint8Array = new Uint8Array(await entry.file.slice(0, 28).arrayBuffer());
  const signature: number[] = [0, 0, 0, 72, 0, 0, 0, 69, 0, 0, 0, 65, 0, 0, 0, 68];
  if (header.length < 28 || !signature.every((byte: number, index: number) => header[index] === byte)) {
    throw new Error('Expected binary RVM with a HEAD header; ASCII RVM is not supported.');
  }
}

async function validateAtt(entry: HsRvmAttFile): Promise<void> {
  let current: string | null = null;
  let records: number = 0;
  let attributes: number = 0;
  let lineNumber: number = 0;
  for await (const line of hsReadProjectFileLines(entry.file)) {
    lineNumber++;
    if (line.includes('\0')) {
      throw new Error(`Line ${lineNumber}: ATT must be UTF-8 text, not binary/UTF-16.`);
    }
    const trimmed: string = line.trim();
    if (/^NEW(?:\s|$)/.test(trimmed)) {
      current = trimmed.slice(3).trim();
      if (current.length === 0) {
        throw new Error(`Line ${lineNumber}: NEW requires a node name.`);
      }
      records++;
    } else if (trimmed === 'END') {
      current = null;
    } else if (trimmed.includes(':=') && current !== null) {
      for (const item of trimmed.split('&end&')) {
        const separator: number = item.indexOf(':=');
        if (separator < 0 || item.slice(0, separator).trim().length === 0) {
          throw new Error(`Line ${lineNumber}: expected an attribute name followed by :=.`);
        }
        attributes++;
      }
    }
  }
  if (records === 0 || attributes === 0) {
    throw new Error('ATT must contain NEW <node name> records with attributes in NAME:= value format.');
  }
}

export async function hsValidateRvmAttProject(files: readonly File[]): Promise<HsRvmAttProject> {
  const result: HsRvmAttProject = { pairs: [], totalBytes: 0, errors: [], warnings: [] };
  const byStem: Map<string, { rvm?: HsRvmAttFile; att?: HsRvmAttFile }> = new Map();
  let ignored: number = 0;
  for (const file of files) {
    const relative: string = file.webkitRelativePath ?? '';
    const path: string = relative.length > 0 ? relative.slice(relative.indexOf('/') + 1) : file.name;
    const match: RegExpExecArray | null = /^(.*)\.(rvm|att)$/i.exec(path);
    if (match === null) {
      ignored++;
      continue;
    }
    if (/[:\\\0\r\n]/.test(path) || path.split('/').some((part: string) => ['', '.', '..'].includes(part))) {
      result.errors.push(`${path}: invalid relative file path.`);
      continue;
    }
    const key: string = match[1].toLowerCase();
    const kind: 'rvm' | 'att' = match[2].toLowerCase() === 'rvm' ? 'rvm' : 'att';
    const pair: { rvm?: HsRvmAttFile; att?: HsRvmAttFile } = byStem.get(key) ?? {};
    if (pair[kind] !== undefined) {
      result.errors.push(`${path}: ambiguous pair; duplicate ${kind.toUpperCase()} filename ignoring letter case.`);
    } else {
      pair[kind] = { file, path };
    }
    byStem.set(key, pair);
  }
  for (const pair of byStem.values()) {
    if (pair.rvm === undefined) {
      if (pair.att !== undefined) {
        result.errors.push(`${pair.att.path}: missing matching RVM in the same subfolder.`);
      }
      continue;
    }
    const rvm: HsRvmAttFile = pair.rvm;
    const att: HsRvmAttFile | null = pair.att ?? null;
    if (att === null) {
      result.warnings.push(`${rvm.path}: no matching ATT; model will be imported without ATT attributes.`);
    }
    result.pairs.push({
      name: rvm.path.replace(/\.rvm$/i, ''),
      rvm,
      att,
      processingId: null,
      error: null,
    });
    result.totalBytes += rvm.file.size + (att?.file.size ?? 0);
    for (const entry of att === null ? [rvm] : [rvm, att]) {
      try {
        if (entry.file.size === 0) {
          throw new Error('File is empty.');
        }
        if (entry === rvm) {
          await validateRvm(entry);
        } else {
          await validateAtt(entry);
        }
      } catch (error: unknown) {
        const message: string = error instanceof Error ? error.message : String(error);
        result.errors.push(`${entry.path}: ${message} Export ATT as UTF-8 and use binary RVM.`);
      }
    }
  }
  if (byStem.size === 0) {
    result.errors.push('Select a folder containing RVM files with optional matching ATT files.');
  }
  if (ignored > 0) {
    result.warnings.push(`${ignored} unrelated files will not be uploaded.`);
  }
  return result;
}
